/**
 * <AntiLockInGuarantee /> — "No vendor lock-in. Walk away with your code intact."
 *
 * v34.68 (Jun 3 2026) — Nova's #1 enterprise unlock (round 4):
 * "If I route all my LLM traffic through your proxy and you go down, my company
 *  dies. State explicitly: leaving CARTIE = swap the base_url back. Nothing else."
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Code2, Database, X, ArrowRight } from 'lucide-react';

const GUARANTEES = [
  {
    icon: Code2,
    title: 'No proprietary SDKs',
    body: 'You use the official OpenAI / Anthropic / Google SDKs. We only change one URL. Your code is the same code with or without us.',
  },
  {
    icon: Database,
    title: 'Your data is yours',
    body: 'We only process anonymized metadata. Your prompts, your customer data, your traces — they live in your VPC. We never see them.',
  },
  {
    icon: X,
    title: 'One line to leave',
    body: 'Want out? Change `base_url` back to `api.openai.com` (or your provider). Done. No migration. No rewrites. No ghost dependencies.',
  },
  {
    icon: Shield,
    title: '24h export, always free',
    body: 'Your entire CARTIE history exports as JSON/CSV in under 24 hours, no questions asked. Forever, even on cancelled plans.',
  },
];

const AntiLockInGuarantee = () => (
  <section
    className="relative py-16 px-4 bg-slate-950 border-y border-emerald-500/20"
    data-testid="anti-lock-in-section"
  >
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[300px] rounded-full bg-emerald-500/[0.05] blur-[120px]" />
    </div>

    <div className="max-w-5xl mx-auto relative">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-[11px] font-mono uppercase tracking-[0.18em] text-emerald-300 mb-4">
          <Shield className="w-3 h-3" /> Anti-lock-in guarantee
        </div>
        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-[-0.02em] leading-tight text-white mb-3">
          You can <span className="text-emerald-400">leave us in one line of code</span>.
        </h2>
        <p className="text-sm text-slate-400 max-w-2xl mx-auto leading-relaxed">
          We refuse to be the kind of vendor that holds your stack hostage. If we stop being worth the price,
          here's exactly how you walk away — with your code, your data, and your dignity intact.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-8">
        {GUARANTEES.map((g, i) => {
          const Icon = g.icon;
          return (
            <div
              key={i}
              className="rounded-xl border border-white/10 bg-slate-900 p-5 hover:border-emerald-400/40 transition-colors"
              data-testid={`anti-lock-in-card-${i}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-white drop-shadow-[0_0_4px_rgba(255,255,255,0.55)]" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white mb-1.5">{g.title}</h3>
                  <p className="text-[13px] text-slate-400 leading-relaxed">{g.body}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Code preview — the "leaving CARTIE" snippet */}
      <div className="rounded-2xl border border-emerald-500/20 bg-slate-950 overflow-hidden">
        <div className="px-4 py-2 border-b border-white/5 bg-slate-900/60 flex items-center justify-between">
          <span className="text-[11px] font-mono text-slate-400">
            <span className="text-emerald-300">// </span>leaving CARTIE · the entire migration
          </span>
          <span className="text-[10px] font-mono text-emerald-300 uppercase tracking-wider">contractually binding</span>
        </div>
        <pre className="px-5 py-5 font-mono text-[12.5px] text-slate-300 overflow-x-auto leading-relaxed">
{`# Before — using CARTIE
import openai
client = openai.OpenAI(
    base_url=`}<span className="text-emerald-300">{`"https://proxy.cartieai.com/v1"`}</span>{`,
    api_key=os.environ["OPENAI_API_KEY"],
)

# After — leaving CARTIE (one line changed)
import openai
client = openai.OpenAI(
    base_url=`}<span className="text-rose-300">{`"https://api.openai.com/v1"`}</span>{`,  # ← that's it
    api_key=os.environ["OPENAI_API_KEY"],
)`}
        </pre>
      </div>

      <p className="text-center text-xs text-slate-500 mt-6 max-w-md mx-auto leading-relaxed">
        Written into every paid contract. No exceptions, no fine print, no <em className="text-slate-400">"unless..."</em>.
      </p>
      <div className="mt-6 flex justify-center">
        <Link
          to="/security"
          data-testid="anti-lock-in-cta"
          className="group inline-flex items-center gap-2 text-sm font-semibold text-emerald-300 hover:text-emerald-200 transition-colors"
        >
          Read the full anti-lock-in contract
          <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
        </Link>
      </div>
    </div>
  </section>
);

export default AntiLockInGuarantee;
